import { IpgSystemIdKey, IpgSystemIdKeyList, IpwSystemIdKey, IpwSystemIdKeyList } from "./types/third-party-system.enums";

import { z } from 'zod';

export const RpaModuleName = {
    PCPARTPICKER_IPW: 'pcpartpicker-ipw' as const,
    DEALERTRACK_IPW: 'dealertrack-ipw' as const,
    VALORPAYTECH_IPG: 'valorpaytech-ipg' as const,
    PCPARTPICKER_WORKFLOWS: 'pcpartpicker-workflows' as const,
    VALORPAYTECH_WORKFLOWS: 'valorpaytech-workflows' as const,
    STONEPROFITS_WORKFLOWS: 'stoneprofits-workflows' as const,
    GENERIC_WEBSITE_WORKFLOWS: 'generic-website-workflows' as const,
} as const;
export type RpaModuleName = (typeof RpaModuleName)[keyof typeof RpaModuleName];
export const ALL_RPA_MODULE_NAMES: RpaModuleName[] = Object.values(RpaModuleName);
const RpaModuleNameValueSet: ReadonlySet<string> = new Set(ALL_RPA_MODULE_NAMES);
export const RpaModuleNameSchema: z.ZodType<RpaModuleName> = z.enum(
    ALL_RPA_MODULE_NAMES
);

export function isRpaModuleName(moduleName: string): moduleName is RpaModuleName {
    return RpaModuleNameValueSet.has(moduleName);
}

export const SessionPersistenceType = {
    EPHEMERAL: 'ephemeral' as const,
    PERSISTENT: 'persistent' as const,
} as const;
export type SessionPersistenceType =
    (typeof SessionPersistenceType)[keyof typeof SessionPersistenceType];

export const IpWebsiteType = {
    PORTAL: 'portal' as const,
    IPG: 'ipg' as const,
    IPW: 'ipw' as const,
} as const;
export type IpWebsiteType = (typeof IpWebsiteType)[keyof typeof IpWebsiteType];

export const MerchantWebsiteType = {
    IPG: IpWebsiteType.IPG,
    IPW: IpWebsiteType.IPW,
} as const;
export type MerchantWebsiteType = (typeof MerchantWebsiteType)[keyof typeof MerchantWebsiteType];
// Zod schema for SessionPersistenceType
export const SessionPersistenceTypeSchema = z.union([
    z.literal('ephemeral'),
    z.literal('persistent'),
]);

export interface AllowedWebsiteBase {
    name: string;
    url: string;
    allowedUrlPatterns: string[];
    sessionPersistence: SessionPersistenceType;
}

export type MerchantAllowedWebsite = AllowedWebsiteBase & { type: MerchantWebsiteType };
export type IpwAllowedWebiste = AllowedWebsiteBase & { type: typeof IpWebsiteType.IPW };
export type IpgAllowedWebsite = AllowedWebsiteBase & { type: typeof IpWebsiteType.IPG };
export type PortalAllowedWebsite = AllowedWebsiteBase & { type: typeof IpWebsiteType.PORTAL };
export type AllowedWebsite = MerchantAllowedWebsite | PortalAllowedWebsite;

// Zod schema for AllowedWebsite
export const AllowedWebsiteSchemaBase = z.object({
    name: z.string(),
    url: z.string(),
    allowedUrlPatterns: z.array(z.string()),
    sessionPersistence: SessionPersistenceTypeSchema,
});

export const AllowedWebsiteSchemaIPG = AllowedWebsiteSchemaBase.extend({
    type: z.literal(MerchantWebsiteType.IPG).default(MerchantWebsiteType.IPG),
});

export const AllowedWebsiteSchemaIPW = AllowedWebsiteSchemaBase.extend({
    type: z.literal(MerchantWebsiteType.IPW).default(MerchantWebsiteType.IPW),
});

// Zod schema for IpwConfig
export const IpwConfigBaseSchema = z.object({
    ipw_id: z.number(),
    ipw_system_id: z.enum(IpwSystemIdKeyList),
    name: z.string(),
    urlPattern: z.string(),
    workflow_prefix: z.string(),
    allowedWebsites: z.array(AllowedWebsiteSchemaIPW).default([]),
    rpaModules: z.array(RpaModuleNameSchema).default([]),
});

export type IpwConfigBase = z.infer<typeof IpwConfigBaseSchema>;


export const IpwConfigDealertrackSchema = IpwConfigBaseSchema.extend({
    ipw_system_id: z.literal(IpwSystemIdKey.DEALERTRACK),
    reconciliation: z.object({
        paymentCode: z.string().min(1).max(2),
        doInjectApprovalNumber: z.boolean(),
        doFinalizeReconciliation: z.boolean(),
    }),
});

export const IpwConfigPcpartpickerSchema = IpwConfigBaseSchema.extend({
    ipw_system_id: z.literal(IpwSystemIdKey.PCPARTPICKER),
});

export type IpwConfigDealertrack = z.infer<typeof IpwConfigDealertrackSchema>;

export const IpwConfigSchema = z.discriminatedUnion('ipw_system_id', [
    IpwConfigPcpartpickerSchema,
    IpwConfigDealertrackSchema,
]);

export type IpwConfig = z.infer<typeof IpwConfigSchema>;


// Zod schema for IpgConfig
export const IpgConfigSchema = z.object({
    ipg_id: z.number(),
    name: z.string(),
    urlPattern: z.string(),
    workflow_prefix: z.string(),
    allowedWebsites: z.array(AllowedWebsiteSchemaIPG).default([]),
    rpaModules: z.array(RpaModuleNameSchema).default([]),
});

export type IpgConfig = z.infer<typeof IpgConfigSchema>;

// Zod schema for SambaConfig
export const SambaConfigSchema = z.object({
    rpaHeadless: z.boolean().default(true),
});
export type SambaConfig = z.infer<typeof SambaConfigSchema>;

export const CustomerPortalDeliveryChannel = {
    EMAIL: 'EMAIL' as const,
    SMS: 'SMS' as const,
} as const;
export type CustomerPortalDeliveryChannel =
    (typeof CustomerPortalDeliveryChannel)[keyof typeof CustomerPortalDeliveryChannel];

export const CustomerPortalDeliveryChannelSchema = z.enum(CustomerPortalDeliveryChannel);

export const E164PhoneNumberSchema = z.string().regex(/^\+[1-9]\d{1,14}$/);
export type E164PhoneNumber = z.infer<typeof E164PhoneNumberSchema>;

export const PaymentRequestDocumentPolicySchema = z.object({
    allowUploadedDocuments: z.boolean().default(true),
    allowGeneratedDocuments: z.boolean().default(true),
    requireDocument: z.boolean().default(false),
});
export type PaymentRequestDocumentPolicy = z.infer<typeof PaymentRequestDocumentPolicySchema>;

export const PaymentRequestTemplateSelectionSchema = z.object({
    paymentRequestDetailsTypeId: z.string().min(1),
    paymentRequestDetailsTypeVersion: z.number().int().nonnegative(),
    documentTemplateUuid: z.string().uuid(),
});
export type PaymentRequestTemplateSelection = z.infer<
    typeof PaymentRequestTemplateSelectionSchema
>;

const CustomerPortalTwilioSmsDeliveryConfigWithFromPhoneNumberSchema = z.object({
    enabled: z.boolean().default(false),
    fromPhoneNumber: E164PhoneNumberSchema,
    messagingServiceSid: z.undefined().optional(),
});

const CustomerPortalTwilioSmsDeliveryConfigWithMessagingServiceSidSchema = z.object({
    enabled: z.boolean().default(false),
    fromPhoneNumber: z.undefined().optional(),
    messagingServiceSid: z.string().min(1),
});

export const CustomerPortalTwilioSmsDeliveryConfigSchema = z.union([
    CustomerPortalTwilioSmsDeliveryConfigWithFromPhoneNumberSchema,
    CustomerPortalTwilioSmsDeliveryConfigWithMessagingServiceSidSchema,
]);
export type CustomerPortalTwilioSmsDeliveryConfig = z.infer<
    typeof CustomerPortalTwilioSmsDeliveryConfigSchema
>;

export const CustomerPortalSendgridEmailDeliveryConfigSchema = z.object({
    enabled: z.boolean().default(false),
    fromEmail: z.string().email(),
    fromName: z.string().min(1).optional(),
    replyToEmail: z.string().email().optional(),
});
export type CustomerPortalSendgridEmailDeliveryConfig = z.infer<
    typeof CustomerPortalSendgridEmailDeliveryConfigSchema
>;

export const CustomerPortalPaymentRequestsConfigSchema = z.object({
    enabled: z.boolean().default(false),
    allowedDeliveryChannels: z.array(CustomerPortalDeliveryChannelSchema).default([]),
    documentPolicy: PaymentRequestDocumentPolicySchema.optional(),
    templateSelections: z.array(PaymentRequestTemplateSelectionSchema).default([]),
    twilioSms: CustomerPortalTwilioSmsDeliveryConfigSchema.optional(),
    sendgridEmail: CustomerPortalSendgridEmailDeliveryConfigSchema.optional(),
});
export type CustomerPortalPaymentRequestsConfig = z.infer<
    typeof CustomerPortalPaymentRequestsConfigSchema
>;

export const CustomerPortalConfigSchema = z.object({
    paymentRequests: CustomerPortalPaymentRequestsConfigSchema.optional(),
});
export type CustomerPortalConfig = z.infer<typeof CustomerPortalConfigSchema>;

export const IpgSystemIdSchema = z.enum(IpgSystemIdKeyList);
export const IpwSystemIdSchema = z.enum(IpwSystemIdKeyList);

export const USStateKeyList = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
    'DC',
] as const;
export const USStateSchema = z.enum(USStateKeyList);
export type USState = z.infer<typeof USStateSchema>;

export const CanadaProvinceKeyList = [
  'AB', // Alberta
  'BC', // British Columbia
  'MB', // Manitoba
  'NB', // New Brunswick
  'NL', // Newfoundland and Labrador
  'NS', // Nova Scotia
  'NT', // Northwest Territories
  'NU', // Nunavut
  'ON', // Ontario
  'PE', // Prince Edward Island
  'QC', // Quebec
  'SK', // Saskatchewan
  'YT', // Yukon
];
export const CanadaProvinceSchema = z.enum(CanadaProvinceKeyList);
export type CanadaProvince = z.infer<typeof CanadaProvinceSchema>;


export const USZipCode5Schema = z.string().regex(/^\d{5}$/);
export const USZipCode5Plus4Schema = z.string().regex(/^\d{5}-\d{4}$/);
export const USZipCodeSchema = z.union([USZipCode5Schema, USZipCode5Plus4Schema]);
export type USZipCode = z.infer<typeof USZipCodeSchema>;

export const normalizePhoneNumber = (value: string): string => value.replace(/[^0-9]/g, '');

export const formatPhoneNumber = (value: string): string => {
    const normalizedDigits: string = normalizePhoneNumber(value);
    const digits: string =
        normalizedDigits.length >= 11 && normalizedDigits.startsWith('1')
            ? normalizedDigits.slice(1, 11)
            : normalizedDigits.slice(0, 10);
    if (digits.length === 0) {
        return '';
    }
    if (digits.length <= 3) {
        return `(${digits}`;
    }
    if (digits.length <= 6) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const normalizeNorthAmericanPhoneNumberToE164 = (value: string): string => {
    const trimmedValue: string = value.trim();
    const normalizedDigits: string = normalizePhoneNumber(trimmedValue);

    if (trimmedValue.length === 0) {
        return '';
    }

    if (trimmedValue.startsWith('+')) {
        return normalizedDigits.length === 0 ? '' : `+${normalizedDigits}`;
    }

    if (normalizedDigits.length === 10) {
        return `+1${normalizedDigits}`;
    }

    if (normalizedDigits.length === 11 && normalizedDigits.startsWith('1')) {
        return `+${normalizedDigits}`;
    }

    return trimmedValue;
};

export const isNormalizedPhoneNumberValid = (value: string): boolean =>
    normalizePhoneNumber(value).length === 10;

export const getPhoneNumberValidationMessage = (value: string): string | null => {
    const trimmedValue: string = value.trim();
    if (trimmedValue.length === 0) {
        return null;
    }
    if (!isNormalizedPhoneNumberValid(trimmedValue)) {
        return 'Phone number must be 10 digits.';
    }
    return null;
};

export const normalizeUSZipCode = (value: string): string => {
    const digits: string = value.replace(/[^0-9]/g, '').slice(0, 9);
    if (digits.length <= 5) {
        return digits;
    }
    return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

export const CanadaPostalCodeSchema = z.string().regex(
  /^[A-Z]\d[A-Z] ?\d[A-Z]\d$/i,
  "Invalid Canadian Postal Code (Expected Format: A1A 1A1)"
);
export type CanadaPostalCode = z.infer<typeof CanadaPostalCodeSchema>;

export const normalizeCanadaPostalCode = (value: string): string => {
    const normalizedChars: string[] = [];
    const upperValue: string = value.toUpperCase();
    for (const char of upperValue) {
        if (normalizedChars.length >= 6) {
            break;
        }
        const position: number = normalizedChars.length;
        const expectsLetter: boolean = position % 2 === 0;
        if (expectsLetter) {
            if (!/[A-Z]/.test(char)) {
                continue;
            }
            normalizedChars.push(char);
            continue;
        }
        if (!/[0-9]/.test(char)) {
            continue;
        }
        normalizedChars.push(char);
    }
    const compact: string = normalizedChars.join('');
    if (compact.length <= 3) {
        return compact;
    }
    return `${compact.slice(0, 3)} ${compact.slice(3)}`;
};

export const AddressUSSchema = z.object({
    street: z.string(),
    street2: z.string().optional(),
    city: z.string(),
    state: USStateSchema,
    zip: USZipCodeSchema,
});

export type AddressUS = z.infer<typeof AddressUSSchema>;

export const AddressCanadaSchema = z.object({
    street: z.string(),
    street2: z.string().optional(),
    city: z.string(),
    province: CanadaProvinceSchema,
    postal_code: CanadaPostalCodeSchema,
});

export type AddressCanada = z.infer<typeof AddressCanadaSchema>;

export type BusinessDetailsCore = {
    legalEntityName: string;
    dba?: string;
    department?: string;
    main_phone_number?: string;
}

export const BusinessDetailsCoreSchema = z.object({
    legalEntityName: z.string(),
    dba: z.string().optional(),
    department: z.string().optional(),
    main_phone_number: z.string().optional(),
})

export type BusinessDetailsUS = BusinessDetailsCore & {
    country: 'US';
    defaultCurrency: 'USD',
    address?: Partial<AddressUS>;
};

export const BusinessDetailsUSSchema = BusinessDetailsCoreSchema.extend({
    country: z.literal('US'),
    defaultCurrency: z.literal('USD').default('USD'),
    address: AddressUSSchema.partial().optional(),
});

export type BusinessDetailsCanada = BusinessDetailsCore & {
    country: 'CA';
    defaultCurrency: 'CAD',
    address?: Partial<AddressCanada>;
};

export const BusinessDetailsCanadaSchema = BusinessDetailsCoreSchema.extend({
    country: z.literal('CA'),
    defaultCurrency: z.literal('CAD').default('CAD'),
    address: AddressCanadaSchema.partial().optional(),
});

export type BusinessDetails = BusinessDetailsUS | BusinessDetailsCanada;

export const BusinessDetailsSchema = z.discriminatedUnion('country', [
    BusinessDetailsUSSchema,
    BusinessDetailsCanadaSchema,
]);

export type PlatformPaymentMethods = {
    terminal: boolean;
    card: boolean;
    ach: boolean;
    e_invoice: boolean;
}

export const PlatformPaymentMethodsSchema: z.ZodType<PlatformPaymentMethods> = z.object({
    terminal: z.boolean().default(false),
    card: z.boolean().default(false),
    ach: z.boolean().default(false),
    e_invoice: z.boolean().default(false),
});

export type PlatformFeatures = {
    payment_methods: PlatformPaymentMethods;
    customerPortal: CustomerPortalConfig | null;
}

export const PlatformFeaturesSchema: z.ZodType<PlatformFeatures> = z.object({
    payment_methods: PlatformPaymentMethodsSchema.default({
        terminal: false,
        card: false,
        ach: false,
        e_invoice: false,
    }),
    customerPortal: CustomerPortalConfigSchema.nullable().default(null),
});

export type PlatformConfig = {
    features: PlatformFeatures;
    payment_request_email_cc: string[];
}

export const PlatformConfigSchema: z.ZodType<PlatformConfig> = z.object({
    features: PlatformFeaturesSchema,
    payment_request_email_cc: z.array(z.string().email()).default([]),
});


export type ProcessorConfig = {
    reporting_dashboard?: IpgAllowedWebsite,
}

export const ProcessorConfigSchema: z.ZodType<ProcessorConfig> = z.object({
    reporting_dashboard: AllowedWebsiteSchemaIPG.optional()
})

export type MerchantUserRoleAssignmentConfig = {
    organizationKind: 'MERCHANT',
    defaultPhysicalTerminalDevice?: string, // UUID
    defaultVirtualTerminalDevice?: string, // UUID
    lastPhysicalTerminalDevice?: string, // UUID
    lastVirtualTerminalDevice?: string, // UUID
}
export type IsoUserRoleAssignmentConfig = {
    organizationKind: 'ISO'
}
export type CustomerUserRoleAssignmentConfig = {
    organizationKind: 'CUSTOMER'
}
export type UserRoleAssignmentConfig = MerchantUserRoleAssignmentConfig | IsoUserRoleAssignmentConfig | CustomerUserRoleAssignmentConfig;

export type MerchantUserRoleAssignmentConfigPatch = {
    organizationKind?: 'MERCHANT';
    defaultPhysicalTerminalDevice?: string;
    defaultVirtualTerminalDevice?: string;
    lastPhysicalTerminalDevice?: string;
    lastVirtualTerminalDevice?: string;
};
export type IsoUserRoleAssignmentConfigPatch = {
    organizationKind?: 'ISO';
};
export type CustomerUserRoleAssignmentConfigPatch = {
    organizationKind?: 'CUSTOMER';
};
export type UserRoleAssignmentConfigPatch =
    | MerchantUserRoleAssignmentConfigPatch
    | IsoUserRoleAssignmentConfigPatch
    | CustomerUserRoleAssignmentConfigPatch;

export const MerchantUserRoleAssignmentConfigSchema: z.ZodObject<{
    organizationKind: z.ZodLiteral<'MERCHANT'>;
    defaultPhysicalTerminalDevice: z.ZodOptional<z.ZodString>;
    defaultVirtualTerminalDevice: z.ZodOptional<z.ZodString>;
    lastPhysicalTerminalDevice: z.ZodOptional<z.ZodString>;
    lastVirtualTerminalDevice: z.ZodOptional<z.ZodString>;
}> = z.object({
    organizationKind: z.literal('MERCHANT'),
    defaultPhysicalTerminalDevice: z.string().uuid().optional(),
    defaultVirtualTerminalDevice: z.string().uuid().optional(),
    lastPhysicalTerminalDevice: z.string().uuid().optional(),
    lastVirtualTerminalDevice: z.string().uuid().optional(),
});
export const IsoUserRoleAssignmentConfigSchema: z.ZodObject<{
    organizationKind: z.ZodLiteral<'ISO'>;
}> = z.object({
    organizationKind: z.literal('ISO'),
});
export const CustomerUserRoleAssignmentConfigSchema: z.ZodObject<{
    organizationKind: z.ZodLiteral<'CUSTOMER'>;
}> = z.object({
    organizationKind: z.literal('CUSTOMER'),
});
export const UserRoleAssignmentConfigSchema: z.ZodDiscriminatedUnion<
    [
        typeof MerchantUserRoleAssignmentConfigSchema,
        typeof IsoUserRoleAssignmentConfigSchema,
        typeof CustomerUserRoleAssignmentConfigSchema,
    ],
    'organizationKind'
> = z.discriminatedUnion('organizationKind', [
    MerchantUserRoleAssignmentConfigSchema,
    IsoUserRoleAssignmentConfigSchema,
    CustomerUserRoleAssignmentConfigSchema,
]);

export const MerchantUserRoleAssignmentConfigPatchSchema: z.ZodObject<{
    organizationKind: z.ZodOptional<z.ZodLiteral<'MERCHANT'>>;
    defaultPhysicalTerminalDevice: z.ZodOptional<z.ZodString>;
    defaultVirtualTerminalDevice: z.ZodOptional<z.ZodString>;
    lastPhysicalTerminalDevice: z.ZodOptional<z.ZodString>;
    lastVirtualTerminalDevice: z.ZodOptional<z.ZodString>;
}> = z
    .object({
        organizationKind: z.literal('MERCHANT').optional(),
        defaultPhysicalTerminalDevice: z.string().uuid().optional(),
        defaultVirtualTerminalDevice: z.string().uuid().optional(),
        lastPhysicalTerminalDevice: z.string().uuid().optional(),
        lastVirtualTerminalDevice: z.string().uuid().optional(),
    })
    .strict();

export const IsoUserRoleAssignmentConfigPatchSchema: z.ZodObject<{
    organizationKind: z.ZodOptional<z.ZodLiteral<'ISO'>>;
}> = z
    .object({
        organizationKind: z.literal('ISO').optional(),
    })
    .strict();

export const CustomerUserRoleAssignmentConfigPatchSchema: z.ZodObject<{
    organizationKind: z.ZodOptional<z.ZodLiteral<'CUSTOMER'>>;
}> = z
    .object({
        organizationKind: z.literal('CUSTOMER').optional(),
    })
    .strict();

export const UserRoleAssignmentConfigPatchSchema: z.ZodUnion<
    [
        typeof MerchantUserRoleAssignmentConfigPatchSchema,
        typeof IsoUserRoleAssignmentConfigPatchSchema,
        typeof CustomerUserRoleAssignmentConfigPatchSchema,
    ]
> = z.union([
    MerchantUserRoleAssignmentConfigPatchSchema,
    IsoUserRoleAssignmentConfigPatchSchema,
    CustomerUserRoleAssignmentConfigPatchSchema,
]);

export type MerchantConfig = {
    businessDetails?: BusinessDetails;
    ipg: Partial<Record<IpgSystemIdKey, IpgConfig>>;
    ipw: Partial<Record<IpwSystemIdKey, IpwConfig>>;
    processorConfig: ProcessorConfig[];
    sambaConfig?: SambaConfig;
    platformConfig?: PlatformConfig;
    userRoleAssignmentConfig?: UserRoleAssignmentConfig;
}

export const MerchantConfigSchema: z.ZodType<MerchantConfig> = z.object({
    businessDetails: BusinessDetailsSchema.optional(),
    ipg: z.partialRecord(IpgSystemIdSchema, IpgConfigSchema).default({}),
    ipw: z.partialRecord(IpwSystemIdSchema, IpwConfigSchema).default({}),
    processorConfig: z.array(ProcessorConfigSchema).default([]),
    sambaConfig: SambaConfigSchema.optional(),
    platformConfig: PlatformConfigSchema.optional(),
    userRoleAssignmentConfig: UserRoleAssignmentConfigSchema.optional(),
});

function allowedWebsiteKey(allowedWebsite: AllowedWebsite): string {
    return [
        allowedWebsite.type,
        allowedWebsite.name,
        allowedWebsite.url,
        allowedWebsite.sessionPersistence,
        allowedWebsite.allowedUrlPatterns.join('\u0000'),
    ].join('\u0001');
}

export function allowedWebsitesOfMerchantConfig(merchantConfig: MerchantConfig): AllowedWebsite[] {
    const combinedAllowedWebsites: AllowedWebsite[] = [
        ...Object.values(merchantConfig.ipg).flatMap((ipg) => ipg.allowedWebsites),
        ...Object.values(merchantConfig.ipw).flatMap((ipw) => ipw.allowedWebsites),
        ...merchantConfig.processorConfig.flatMap(
            (processorConfig): AllowedWebsite[] =>
                processorConfig.reporting_dashboard === undefined
                    ? []
                    : [processorConfig.reporting_dashboard]
        ),
    ];
    const dedupedAllowedWebsites = new Map<string, AllowedWebsite>();

    for (const allowedWebsite of combinedAllowedWebsites) {
        dedupedAllowedWebsites.set(allowedWebsiteKey(allowedWebsite), allowedWebsite);
    }

    return Array.from(dedupedAllowedWebsites.values());
}

export function rpaModulesOfMerchantConfig(merchantConfig: MerchantConfig): RpaModuleName[] {
    return [
        ...Object.values(merchantConfig.ipg).flatMap((ipg) => ipg.rpaModules),
        ...Object.values(merchantConfig.ipw).flatMap((ipw) => ipw.rpaModules),
    ]
}
